import { useMemo } from 'react';
import firebase from '../../../firebase/compat';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { db } from '../../../firebase/config.js';
import { useFirestore } from '../../../firebase/useFirestore';
import { STANDARDIZED_DATA_FIELDS } from '../../../helpers/standardizedDataFields';
import resolveHouseholdColumns, {
    HOUSEHOLD_COLUMN_CATALOG,
    COLUMN_OWNED_TITLES,
    columnHidden,
} from '../../../helpers/adressHelpers/resolveHouseholdColumns';
import useSharedCollection from '../../../firebase/useSharedCollection';
import hasLocationFilter from '../../../helpers/dashboardHelpers/hasLocationFilter';
import ToggleButton from '../../common/ToggleButton';
import Table from '../../common/table/Table.jsx';
import TableCell from '../../common/table/TableCell.jsx';

// Beheerscherm voor de kolommen van het huishoudenoverzicht: bestaande
// kolommen aan of uit, en een eigen gegeven als extra kolom.
//
// AI-CONTEXT: See docs/features/tenant-status-and-column-config.md

/** Het veld op CompagnyMeta waar dit alles in staat. */
const FIELD = 'HouseholdColumns';

/**
 * De titels waaruit een extra kolom gekozen kan worden.
 *
 * **Bewust een keuzelijst en geen tekstveld.** De koppeling tussen een kolom en
 * een gegeven loopt over de TITEL, niet over een id. Een typefout levert dan
 * geen foutmelding op maar een kolom die bij elk huishouden leeg blijft — en
 * dat is niet te onderscheiden van 'niemand heeft dit ingevuld'.
 * AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
 *
 * De platformbrede standaardtitels plus de velden die deze organisatie zelf in
 * haar intakeformulier heeft gezet. Staat een titel er niet bij, dan bestaat
 * het gegeven nog nergens en valt er ook niets te tonen.
 *
 * @param {Array<{Title?: string}>} intakeFields - Rijen uit IntakeFormFields
 * @param {Array<string>} alGekozen - Titels die al een kolom zijn
 * @returns {Array<string>}
 */
const beschikbareTitels = (intakeFields, alGekozen) => {
    const uitFormulier = (Array.isArray(intakeFields) ? intakeFields : [])
        .map((field) => (typeof field?.Title === 'string' ? field.Title.trim() : ''))
        .filter((titel) => titel !== '');

    const bezet = new Set([...COLUMN_OWNED_TITLES, ...alGekozen]);

    return [...new Set([...STANDARDIZED_DATA_FIELDS, ...uitFormulier])]
        .filter((titel) => !bezet.has(titel))
        .sort((a, b) => a.localeCompare(b, 'nl'));
};

/**
 * @returns {JSX.Element}
 */
const HouseholdColumnsConfig = () => {
    const compagny = useFirestore('CompagnyMeta');
    const intakeFields = useFirestore('IntakeFormFields');
    const locations = useSharedCollection('Locations');

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const config = (comp && comp[FIELD]) || {};

    const { extraTitles } = resolveHouseholdColumns(config);

    // De kolom LOCATIE kan alleen verschijnen bij twee of meer locaties (zie
    // `Overview.jsx`). Hem hier toch aanbieden levert een schakelaar op voor
    // iets dat nooit kan gebeuren -- de belofte staat dan op de ene plek en de
    // regel op de andere.
    // AI-CONTEXT: See docs/pitfalls.md#ui-shared-affordance-gated-per-caller
    const kiesbareKolommen = useMemo(
        () => (hasLocationFilter(locations)
            ? HOUSEHOLD_COLUMN_CATALOG
            : HOUSEHOLD_COLUMN_CATALOG.filter((column) => column.key !== 'location')),
        [locations],
    );

    const keuzes = useMemo(
        () => beschikbareTitels(intakeFields, extraTitles),
        // Scheidingsteken als escape-reeks, nooit als echte byte.
        // AI-CONTEXT: See docs/pitfalls.md#dev-escape-sequence-written-as-a-raw-byte
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [intakeFields, extraTitles.join('\n')],
    );

    /** @param {string} titel */
    const voegToe = (titel) => {
        if (!titel) return;
        db.collection('CompagnyMeta').doc(comp.docid).update({
            [`${FIELD}.Extra`]: firebase.firestore.FieldValue.arrayUnion(titel),
        });
    };

    /** @param {string} titel */
    const haalWeg = (titel) => {
        db.collection('CompagnyMeta').doc(comp.docid).update({
            [`${FIELD}.Extra`]: firebase.firestore.FieldValue.arrayRemove(titel),
        });
    };

    // `dashboard-container` en NIET `table-container`: dat laatste was tot
    // 2026-10-14 een scrollvak van 520px hoog, bedoeld om alleen een tabel in te
    // zetten. Alles eronder viel buiten beeld en was alleen nog met een
    // scrollbalk BINNEN de kaart te bereiken. De gedeelde `Table` regelt zijn
    // eigen hoogte, dus er gaat niets verloren.
    // AI-CONTEXT: See docs/pitfalls.md#ui-container-class-is-a-scroll-box
    return (
        <div className="dashboard-container">
            <h2>Kolommen in het overzicht van huishoudens</h2>
            <p>
                Kies welke kolommen je in het overzicht ziet, en voeg een eigen gegeven toe
                als kolom. Dit verandert alleen wat er op je scherm staat — er gaat geen
                gegeven verloren en tellingen en rapportages blijven hetzelfde.
            </p>

            {/* Zolang de instellingen onderweg zijn geen tabel met schakelaars die
                allemaal op 'aan' lijken te staan: dat zou een keuze tonen die de
                organisatie misschien niet gemaakt heeft.
                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                <>
                    <Table
                        columns={[
                            { key: 'label', label: 'KOLOM' },
                            { key: 'hidden', label: 'VERBERGEN', searchable: false },
                        ]}
                        items={kiesbareKolommen}
                        getItemKey={(column) => column.key}
                        showSearch={false}
                        tableClassName="status-config-table"
                        renderRow={(column) => (
                            <>
                                <TableCell>{column.label}</TableCell>
                                <TableCell>
                                    {column.fixed ? (
                                        <span className="status-config-fixed">
                                            Kan niet uit
                                        </span>
                                    ) : (
                                        // 'Verbergen' en niet 'tonen': ToggleButton
                                        // schrijft de stand van de schakelaar recht
                                        // in het veld weg, dus de knop moet hetzelfde
                                        // heten als het veld. Een omgekeerd label zou
                                        // bij elke klik het tegenovergestelde
                                        // opslaan van wat de gebruiker aanwees.
                                        // Zelfde vorm als bij de statussen.
                                        //
                                        // `columnHidden` en niet de kale
                                        // opgeslagen waarde: een kolom mag
                                        // standaard uit staan, en dan is er nog
                                        // niets opgeslagen. De schakelaar zou
                                        // dan 'niet verbergen' tonen terwijl de
                                        // kolom ontbreekt, en de eerste klik
                                        // zou het tegenovergestelde opslaan van
                                        // wat de gebruiker ziet. Dezelfde
                                        // functie die de tabel gebruikt.
                                        <ToggleButton
                                            docid={comp.docid}
                                            id={`${FIELD}.Hidden.${column.key}`}
                                            collection={'CompagnyMeta'}
                                            value={columnHidden(config.Hidden, column.key)}
                                        />
                                    )}
                                </TableCell>
                            </>
                        )}
                    />

                    {/* Geen kolom maar een vorm: dezelfde kolom, minder erin. Daarom
                        niet in de tabel hierboven maar als losse schakelaar eronder. */}
                    <div className="settings-general-module-settings-item-container">
                        <p>Bij &apos;LAATSTE CONTACT&apos; alleen de datum tonen</p>
                        <ToggleButton
                            docid={comp.docid}
                            id={`${FIELD}.ContactDateOnly`}
                            collection={'CompagnyMeta'}
                            value={config.ContactDateOnly}
                        />
                    </div>

                    <h3 className="status-config-subheading">Eigen gegevens als kolom</h3>
                    {extraTitles.length === 0 ? (
                        <p className="status-config-footnote">
                            Nog geen eigen gegeven als kolom toegevoegd.
                        </p>
                    ) : (
                        <ul className="status-config-extra-list">
                            {extraTitles.map((titel) => (
                                <li key={titel}>
                                    <span>{titel}</span>
                                    <DeleteOutlinedIcon
                                        className="table-delete-icon"
                                        onClick={() => haalWeg(titel)}
                                        aria-label={`Kolom '${titel}' weghalen`}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}

                    <label className="status-config-add">
                        <span>Gegeven toevoegen als kolom</span>
                        <select
                            value=""
                            onChange={(e) => voegToe(e.target.value)}
                            aria-label="Gegeven toevoegen als kolom"
                        >
                            <option value="">-- Kies een gegeven --</option>
                            {keuzes.map((titel) => (
                                <option key={titel} value={titel}>{titel}</option>
                            ))}
                        </select>
                    </label>
                </>
            )}
        </div>
    );
};

export default HouseholdColumnsConfig;
